import React, { useState, useEffect } from "react";
import axios from "axios";
import { useAuth } from "../App";
import Avatar from "../components/Avatar";
import { API, typeLabel } from "../api";

const MONTH_NAMES = ["Jan", "Fév", "Mar", "Avr", "Mai", "Jun", "Jul", "Aoû", "Sep", "Oct", "Nov", "Déc"];
const COLORS = { CP: "#B5D4F4", RTT: "#C8E6C9", Maladie: "#FFD180", Maternite: "#F9A8D4", Paternite: "#93C5FD", Pacs: "#FDE68A", default: "#E0E0E0" };

function workingDaysInMonth(year, month) {
  const days = new Date(year, month + 1, 0).getDate();
  let count = 0;
  for (let d = 1; d <= days; d++) {
    const day = new Date(year, month, d).getDay();
    if (day !== 0 && day !== 6) count++;
  }
  return count;
}

function countWorkingDays(from, to) {
  let days = 0;
  for (let d = new Date(from); d <= to; d.setDate(d.getDate() + 1)) {
    if (d.getDay() !== 0 && d.getDay() !== 6) days++;
  }
  return days;
}

function leaveDaysInMonth(leave, year, month) {
  const start = new Date(year, month, 1);
  const end = new Date(year, month + 1, 0);
  const ls = new Date(leave.date_debut);
  const le = new Date(leave.date_fin);
  if (le < start || ls > end) return 0;

  // Congé entièrement dans le mois : utiliser jours stocké en base (source de vérité, inclut fériés)
  if (ls >= start && le <= end) return parseFloat(leave.jours);

  // Congé à cheval sur plusieurs mois : compter directement les jours ouvrés dans la portion du mois
  const from = ls < start ? start : ls;
  const to = le > end ? end : le;
  return countWorkingDays(from, to);
}

function Annuel() {
  const { token, user } = useAuth();
  const [members, setMembers] = useState([]);
  const [leaves, setLeaves] = useState([]);
  const [year, setYear] = useState(new Date().getFullYear());
  const [selected, setSelected] = useState(null); // { uid, month }

  useEffect(() => {
    const headers = { Authorization: "Bearer " + token };
    const usersUrl = user.role === "daf" ? `${API}/users` : `${API}/users/team`;
    const leavesUrl = user.role === "daf" ? `${API}/leaves/cabinet` : `${API}/leaves/team`;
    Promise.all([
      axios.get(usersUrl, { headers }),
      axios.get(leavesUrl, { headers }),
    ]).then(([ur, lr]) => {
      const allUsers = ur.data.filter(u => u.role !== "daf");
      // Mettre l'utilisateur courant en premier si associé
      if (user.role === "associe") {
        const me = allUsers.find(u => u.id === user.id);
        const rest = allUsers.filter(u => u.id !== user.id);
        setMembers(me ? [me, ...rest] : rest);
      } else {
        setMembers(allUsers);
      }
      setLeaves(lr.data.filter(l => l.statut === "approved" && l.type !== "Absence"));
    });
  }, [token, user.role, user.id]);

  const getCell = (uid, month) => {
    const userLeaves = leaves.filter(l => l.user_id === uid && (new Date(l.date_debut).getFullYear() === year || new Date(l.date_fin).getFullYear() === year));
    const monthLeaves = userLeaves.filter(l => leaveDaysInMonth(l, year, month) > 0);
    const total = monthLeaves.reduce((s, l) => s + leaveDaysInMonth(l, year, month), 0);
    const types = [...new Set(monthLeaves.map(l => l.type))];
    return { total, types, monthLeaves };
  };

  const selectedLeaves = selected
    ? leaves.filter(l => l.user_id === selected.uid && leaveDaysInMonth(l, year, selected.month) > 0)
    : [];

  return (
    <div>
      {/* Header */}
      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 20, flexWrap: "wrap", gap: 10 }}>
        <h2 style={{ margin: 0, fontWeight: 600, fontSize: 18 }}>📅 Vue annuelle</h2>
        <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
          <button onClick={() => setYear(y => y - 1)}
            style={{ background: "#f0f0f0", border: "none", borderRadius: 6, padding: "6px 12px", fontSize: 13, cursor: "pointer", color: "#555", fontWeight: 600 }}>‹</button>
          <span style={{ fontSize: 16, fontWeight: 700, color: "#333", minWidth: 60, textAlign: "center" }}>{year}</span>
          <button onClick={() => setYear(y => y + 1)}
            style={{ background: "#f0f0f0", border: "none", borderRadius: 6, padding: "6px 12px", fontSize: 13, cursor: "pointer", color: "#555", fontWeight: 600 }}>›</button>
        </div>
      </div>

      {/* Tableau */}
      <div style={{ overflowX: "auto" }}>
        <table style={{ borderCollapse: "collapse", width: "100%", minWidth: 780 }}>
          <thead>
            <tr style={{ background: "#fafafa" }}>
              <th style={{ padding: "8px 14px", textAlign: "left", fontSize: 12, fontWeight: 600, color: "#888", borderBottom: "2px solid #eee", position: "sticky", left: 0, background: "#fafafa", zIndex: 1, minWidth: 160 }}>
                Collaborateur
              </th>
              {MONTH_NAMES.map((m, i) => (
                <th key={i} style={{ padding: "8px 6px", textAlign: "center", fontSize: 12, fontWeight: 600, color: i === new Date().getMonth() && year === new Date().getFullYear() ? "#7C6FE0" : "#888", borderBottom: "2px solid #eee", minWidth: 52 }}>
                  {m}
                </th>
              ))}
              <th style={{ padding: "8px 10px", textAlign: "center", fontSize: 12, fontWeight: 600, color: "#888", borderBottom: "2px solid #eee", minWidth: 52 }}>
                Total
              </th>
            </tr>
          </thead>
          <tbody>
            {members.map((m, idx) => {
              const yearTotal = Array.from({ length: 12 }, (_, mo) => getCell(m.id, mo).total).reduce((a, b) => a + b, 0);
              return (
                <tr key={m.id} style={{ background: idx % 2 === 0 ? "#fff" : "#fdfcff" }}>
                  <td style={{ padding: "8px 14px", borderBottom: "1px solid #f5f5f5", position: "sticky", left: 0, background: idx % 2 === 0 ? "#fff" : "#fdfcff", zIndex: 1 }}>
                    <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                      <Avatar prenom={m.prenom} nom={m.nom} size={26} fontSize={10} />
                      <span style={{ fontSize: 12, fontWeight: 600, color: "#333" }}>{m.prenom} {m.nom}</span>
                    </div>
                  </td>
                  {Array.from({ length: 12 }, (_, mo) => {
                    const { total, types } = getCell(m.id, mo);
                    const isCurrentMonth = mo === new Date().getMonth() && year === new Date().getFullYear();
                    const isSelected = selected && selected.uid === m.id && selected.month === mo;
                    const bg = total > 0 ? (COLORS[types[0]] || COLORS.default) : "transparent";
                    return (
                      <td key={mo} onClick={() => total > 0 && setSelected(isSelected ? null : { uid: m.id, month: mo })}
                        style={{
                          padding: "6px 4px", textAlign: "center", borderBottom: "1px solid #f5f5f5",
                          cursor: total > 0 ? "pointer" : "default",
                          background: isSelected ? "#7C6FE0" : isCurrentMonth ? "#F3F1FF" : "transparent",
                          borderRadius: 4,
                        }}>
                        {total > 0 ? (
                          <div style={{
                            background: isSelected ? "#fff" : bg,
                            color: isSelected ? "#7C6FE0" : "#333",
                            borderRadius: 6, padding: "3px 4px", fontSize: 11, fontWeight: 700,
                            border: `1px solid ${isSelected ? "#fff" : (COLORS[types[0]] || "#ddd")}`,
                          }}>
                            {total}j
                          </div>
                        ) : (
                          <span style={{ fontSize: 11, color: "#e0e0e0" }}>–</span>
                        )}
                      </td>
                    );
                  })}
                  <td style={{ padding: "6px 10px", textAlign: "center", borderBottom: "1px solid #f5f5f5", fontWeight: 700, fontSize: 12, color: yearTotal > 0 ? "#185FA5" : "#ccc" }}>
                    {yearTotal > 0 ? `${yearTotal}j` : "–"}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      {/* Légende */}
      <div style={{ display: "flex", gap: 14, marginTop: 16, flexWrap: "wrap" }}>
        {Object.entries(COLORS).filter(([k]) => k !== "default").map(([type, color]) => (
          <div key={type} style={{ display: "flex", alignItems: "center", gap: 5, fontSize: 12, color: "#666" }}>
            <div style={{ width: 12, height: 12, background: color, borderRadius: 3, border: "1px solid #ddd" }} />
            {typeLabel(type)}
          </div>
        ))}
      </div>

      {/* Détail mois sélectionné */}
      {selected && selectedLeaves.length > 0 && (
        <div style={{ marginTop: 20, background: "#fff", border: "1px solid #e0e0e0", borderRadius: 12, padding: 20 }}>
          <div style={{ fontSize: 13, fontWeight: 700, color: "#333", marginBottom: 12 }}>
            {members.find(m => m.id === selected.uid)?.prenom} {members.find(m => m.id === selected.uid)?.nom} — {MONTH_NAMES[selected.month]} {year}
          </div>
          <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
            {selectedLeaves.map(l => (
              <div key={l.id} style={{ display: "flex", justifyContent: "space-between", alignItems: "center", padding: "8px 12px", background: "#f9f9fb", borderRadius: 8, fontSize: 13 }}>
                <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                  <div style={{ width: 8, height: 8, borderRadius: "50%", background: COLORS[l.type] || COLORS.default }} />
                  <span style={{ fontWeight: 600 }}>{typeLabel(l.type)}</span>
                  <span style={{ color: "#888" }}>{new Date(l.date_debut).toLocaleDateString("fr-FR")} → {new Date(l.date_fin).toLocaleDateString("fr-FR")}</span>
                </div>
                <span style={{ fontWeight: 700, color: "#185FA5" }}>{leaveDaysInMonth(l, year, selected.month)}j ce mois / {parseFloat(l.jours)}j total</span>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

export default Annuel;
